import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Send Announcement Step
 *
 * The simple, point-and-click way to broadcast an in-app announcement
 * from a workflow. Authors pick recipients (specific users, groups, or
 * everyone) and write a title/body in the config UI. The step takes no
 * runtime data inputs — just a flow signal — so it's safe to drop
 * anywhere in a workflow.
 *
 * Internally this writes a `SystemAnnouncement` notification event via
 * NotificationService. Per-entity event types (InvoiceOverdue,
 * PaymentReceived, …) are still supported by the service layer but are
 * not exposed in the workflow UI today — they'll get their own step
 * (or a dynamic-input variant of this one) when a workflow actually
 * needs them.
 */
export const sendAnnouncementStep = defineStepConfig({
  type: "send_announcement",
  name: "Send Announcement",
  description:
    "Broadcasts an in-app announcement to picked recipients. No runtime " +
    "inputs — title, body, and recipients are all set in the step config.",
  category: "Integration",

  // No data inputs — just the implicit flow signal.
  inputSchema: z.object({}),

  outputSchema: z.object({
    eventId: z.string().uuid().describe("The comm_notification_events.id row."),
    recipientCount: z
      .number()
      .int()
      .nonnegative()
      .describe(
        "Number of UserNotification rows inserted by this run. 0 on " +
          "idempotent replay or when no recipients resolved.",
      ),
    deduplicated: z
      .boolean()
      .describe(
        "True when the call matched an existing event via the auto-generated " +
          "idempotency key (a prior step run already wrote it).",
      ),
  }),

  configSchema: z
    .object({
      recipientKind: z
        .enum(["specific_users", "user_groups", "all_users"])
        .default("specific_users")
        .describe(
          "Who receives the announcement. " +
            "'specific_users' uses recipientUserIds; " +
            "'user_groups' uses recipientUserGroupIds; " +
            "'all_users' picks every active user in the org at send time. " +
            "Defaults to 'specific_users' for configs saved before this " +
            "field was persisted by the editor.",
        ),

      recipientUserIds: z
        .array(z.string().uuid())
        .optional()
        .describe("Used when recipientKind is 'specific_users'."),

      recipientUserGroupIds: z
        .array(z.string().uuid())
        .optional()
        .describe("Used when recipientKind is 'user_groups'."),

      title: z.string().min(1).describe("Announcement headline."),
      body: z.string().min(1).describe("Announcement body text."),
      level: z
        .enum(["info", "warn", "critical"])
        .default("info")
        .describe(
          "Severity tag for the renderer (icon color, prominence). " +
            "Defaults to 'info'.",
        ),

      channels: z
        .array(z.enum(["in_app", "email"]))
        .min(1)
        .default(["in_app"])
        .describe(
          "Delivery channels for this announcement. 'in_app' writes to " +
            "the bell inbox; 'email' sends a plain message via the " +
            "configured sender mailbox. Both can be selected.",
        ),

      senderKind: z
        .enum(["default_sendgrid", "microsoft_graph"])
        .default("default_sendgrid")
        .describe(
          "Email backend used when 'email' is in channels. " +
            "'default_sendgrid' uses the org-wide verified SendGrid " +
            "sender; 'microsoft_graph' sends from a registered mailbox " +
            "UPN.",
        ),

      senderMailbox: z
        .preprocess(
          // The editor persists an empty-string default for the UPN
          // field whenever senderKind flips back to SendGrid. Coerce
          // those into `undefined` so `.optional()` accepts them
          // instead of `.min(1)` rejecting at run time.
          (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
          z.string().min(1).optional(),
        )
        .describe(
          "Mailbox UPN to send from when senderKind is " +
            "'microsoft_graph'. Must be an enabled UPN under one of the " +
            "org's EmailIngestConfigs. Ignored for SendGrid.",
        ),
    })
    // Catch the "configured but empty" case at validation time —
    // without these checks, a workflow run would silently send to zero
    // recipients and look successful.
    .superRefine((val, ctx) => {
      if (
        val.recipientKind === "specific_users" &&
        (!val.recipientUserIds || val.recipientUserIds.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["recipientUserIds"],
          message:
            "recipientUserIds must contain at least one user when " +
            "recipientKind is 'specific_users'.",
        });
      }
      if (
        val.recipientKind === "user_groups" &&
        (!val.recipientUserGroupIds || val.recipientUserGroupIds.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["recipientUserGroupIds"],
          message:
            "recipientUserGroupIds must contain at least one group when " +
            "recipientKind is 'user_groups'.",
        });
      }
      if (
        val.channels.includes("email") &&
        val.senderKind === "microsoft_graph" &&
        !val.senderMailbox
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["senderMailbox"],
          message:
            "senderMailbox is required when senderKind is 'microsoft_graph'.",
        });
      }
    }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
